import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useShallow } from "zustand/react/shallow";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { isAbandoned } from "@/lib/api/organizations";
import { SHELL_PAGES } from "@/lib/navigation/pages";
import {
  spaceAncestors,
  spaceColor,
  useOrgContextStore,
  useVisibleSpacePaths,
  type SpacePath,
} from "@/components/main/stores/orgContextStore";
import { SpaceDot } from "./SpaceDot";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { NewSpaceOverlay } from "./NewSpaceOverlay";
import {
  endFilterOnTree,
  useSpaceFilterResult,
  useSpacesFilterStore,
} from "./spacesFilterStore";
import { highlightSegments } from "@/lib/highlightSegments";

interface SpacesRailProps {
  /// Clicking a space narrows the Overview to it, going there from any other page.
  selectedSpaceId: string | null;
  onSelectSpace: (spaceId: string) => void;
}

interface SpaceNode {
  path: SpacePath;
  children: SpaceNode[];
}

/// `paths` is depth-first, so each row's parent is the last row one level up.
function nestSpacePaths(paths: SpacePath[]): SpaceNode[] {
  const roots: SpaceNode[] = [];
  const open: SpaceNode[] = [];
  for (const path of paths) {
    const node: SpaceNode = { path, children: [] };
    const depth = path.ancestors.length;
    const parent = depth > 0 ? open[depth - 1] : undefined;
    (parent ? parent.children : roots).push(node);
    open.length = depth;
    open[depth] = node;
  }
  return roots;
}

const TURN = "transform var(--duration-medium) var(--ease-move)";

/// The 0fr↔1fr fold `DetailsSection` uses; folded rows stay mounted but inert so
/// they slide back. `clip`, not `hidden`: a scroll container would pin the
/// rows' sticky controls to the fold instead of the rail.
const Fold: React.FC<{ folded: boolean; children: React.ReactNode }> = ({
  folded,
  children,
}) => (
  <div
    className="grid"
    inert={folded}
    style={{
      gridTemplateRows: folded ? "0fr" : "1fr",
      transition: "grid-template-rows var(--duration-medium) var(--ease-move)",
    }}
  >
    <div style={{ minHeight: 0, overflowY: "clip" }}>{children}</div>
  </div>
);

/// The spaces tree under the rail's Spaces row (S6d). Creation is never a quick
/// action: the row's "+" founds a root, a space's "+" a child (D50).
export const SpacesRail = React.memo(function SpacesRail({
  selectedSpaceId,
  onSelectSpace,
}: SpacesRailProps) {
  const [createParent, setCreateParent] = useState<string | null>(null);
  const navigate = useNavigate();
  // The row of the space whose settings are open stays highlighted.
  const settingsSpaceId =
    useMatch("/spaces/:space_id")?.params.space_id ?? null;
  const onOverview = useMatch(SHELL_PAGES.Overview.path) !== null;
  const { spaces, collapsed, treeCollapsed, toggleCollapsed, spacesLoading } =
    useOrgContextStore(
      useShallow((s) => ({
        spaces: s.spaces,
        collapsed: s.collapsed,
        treeCollapsed: s.treeCollapsed,
        toggleCollapsed: s.toggleCollapsed,
        spacesLoading: s.spacesLoading,
      })),
    );
  const paths = useVisibleSpacePaths();
  const filtered = useSpaceFilterResult();
  const { active, setActive } = useSpacesFilterStore(
    useShallow((s) => ({ active: s.active, setActive: s.setActive })),
  );
  const models = useMainStateStore((s) => s.availableModels);
  const litSpaceId = settingsSpaceId ?? (onOverview ? selectedSpaceId : null);
  const holdingLit = useMemo(
    () =>
      new Set(
        litSpaceId
          ? spaceAncestors(spaces, litSpaceId).map((s) => s.space_id)
          : [],
      ),
    [spaces, litSpaceId],
  );

  const countBySpace = useMemo(() => {
    const counts = new Map<string, number>();
    for (const model of models) {
      const spaceId = model.space_id;
      if (spaceId) counts.set(spaceId, (counts.get(spaceId) ?? 0) + 1);
    }
    return counts;
  }, [models]);

  // From the DRAWN set: a doomed child is in the Trash, and a chevron on its
  // parent would expand to nothing.
  const tree = useMemo(() => nestSpacePaths(paths), [paths]);

  const select = (spaceId: string) => {
    onSelectSpace(spaceId);
    if (!onOverview) navigate(SHELL_PAGES.Overview.path);
  };
  const rowProps = (path: SpacePath) => {
    const spaceId = path.space.space_id;
    return {
      path,
      count: countBySpace.get(spaceId) ?? 0,
      isSelected: litSpaceId === spaceId,
      onOpenSettings: () => navigate(`/spaces/${spaceId}`),
      isNamingInside: createParent === spaceId,
      onCreateInside: () => setCreateParent(spaceId),
    };
  };

  const branch = ({ path, children }: SpaceNode): React.ReactNode => {
    const spaceId = path.space.space_id;
    const isCollapsed = !!collapsed[spaceId];
    return (
      <React.Fragment key={spaceId}>
        <SpaceRow
          {...rowProps(path)}
          expandable={children.length > 0}
          isCollapsed={isCollapsed}
          holdsSelection={isCollapsed && holdingLit.has(spaceId)}
          onToggleCollapse={() => toggleCollapsed(spaceId)}
          onSelect={() => select(spaceId)}
        />
        {children.length > 0 && (
          <Fold folded={isCollapsed}>{children.map(branch)}</Fold>
        )}
      </React.Fragment>
    );
  };

  const activeMatch = filtered?.matches[active] ?? filtered?.matches[0];
  const rows = filtered ? (
    filtered.matches.length === 0 ? (
      <span className="flex h-7 items-center pl-10 text-fg-muted-regular">
        No matching space
      </span>
    ) : (
      // Focus stays in the field, so a click on a row never blurs it first.
      <div onMouseDown={(e) => e.preventDefault()}>
        {filtered.rows.map((row) => {
          const spaceId = row.path.space.space_id;
          return (
            <SpaceRow
              key={spaceId}
              {...rowProps(row.path)}
              onSelect={() => {
                endFilterOnTree();
                select(spaceId);
              }}
              match={{
                pattern: filtered.pattern,
                isContext: !row.isMatch,
                isActive: activeMatch === spaceId,
                openBelow: row.openBelow,
                onHover: () => {
                  const index = filtered.matches.indexOf(spaceId);
                  if (index >= 0) setActive(index);
                },
              }}
            />
          );
        })}
      </div>
    )
  ) : spacesLoading && spaces.length === 0 ? (
    <span className="flex h-7 items-center pl-10 text-fg-muted-regular-small">
      Loading…
    </span>
  ) : (
    tree.map(branch)
  );

  return (
    <>
      {/* A filter shows its matches even under a collapsed tree. */}
      <Fold folded={treeCollapsed && !filtered}>
        {/* The rail scrolls a deep tree sideways; rows as wide as the widest. */}
        <div className="flex w-max min-w-full flex-col">{rows}</div>
        <div className="h-2" />
      </Fold>

      <NewSpaceOverlay
        open={createParent !== null}
        onClose={() => setCreateParent(null)}
        parentId={createParent}
      />
    </>
  );
});

/// The Spaces row's controls: filtering the tree, and a new root space.
export const SpacesRootActions: React.FC = () => {
  const [creating, setCreating] = useState(false);
  const openFilter = useSpacesFilterStore((s) => s.open);
  const revealOnHover =
    "opacity-0 transition-opacity group-hover/rail-item:opacity-100 has-[:focus-visible]:opacity-100";

  return (
    <span className="flex items-center gap-1">
      <span className={revealOnHover}>
        <TooltipButton
          title="Filter spaces"
          aria-label="Filter spaces"
          variant="contextual"
          className="rail-action"
          size="xs"
          onClick={openFilter}
        >
          <SearchIcon fontSize="inherit" />
        </TooltipButton>
      </span>
      <span className={creating ? "" : revealOnHover}>
        <TooltipButton
          title="New space"
          aria-label="New space"
          aria-haspopup="dialog"
          aria-expanded={creating}
          variant="contextual"
          className="rail-action"
          size="xs"
          onClick={() => setCreating(true)}
        >
          <AddIcon fontSize="inherit" />
        </TooltipButton>
      </span>
      <NewSpaceOverlay
        open={creating}
        onClose={() => setCreating(false)}
        parentId={null}
      />
    </span>
  );
};

/// Laid over the Spaces row's icon: hovered, the icon fades into a chevron that
/// folds or opens the whole tree, as a canvas directory section's does. No
/// fill, so the icon itself reads as changing.
export const SpacesFoldToggle: React.FC = () => {
  const { treeCollapsed, toggleTreeCollapsed } = useOrgContextStore(
    useShallow((s) => ({
      treeCollapsed: s.treeCollapsed,
      toggleTreeCollapsed: s.toggleTreeCollapsed,
    })),
  );
  return (
    <Tooltip title={treeCollapsed ? "Expand all" : "Collapse all"} instant>
      <button
        aria-label={treeCollapsed ? "Expand all spaces" : "Collapse all spaces"}
        aria-expanded={!treeCollapsed}
        onClick={toggleTreeCollapsed}
        className="group/fold relative flex h-8 w-8 items-center justify-center"
      >
        <DashboardOutlinedIcon className="icon-md transition-opacity group-hover/fold:opacity-0 group-focus-visible/fold:opacity-0" />
        <ChevronRightIcon
          className="icon-md absolute opacity-0 group-hover/fold:opacity-100 group-focus-visible/fold:opacity-100"
          sx={{
            transform: treeCollapsed ? "none" : "rotate(90deg)",
            transition: `${TURN}, opacity var(--duration-short) var(--ease-move)`,
          }}
        />
      </button>
    </Tooltip>
  );
};

/// The row's narrowed-by-filter state: a match or a parent kept for its path.
interface RowMatch {
  pattern: RegExp;
  isContext: boolean;
  /// The match ↵ opens; lit as hover is, since the pointer moves it too.
  isActive: boolean;
  openBelow: boolean;
  onHover: () => void;
}

const SpaceRow: React.FC<{
  path: SpacePath;
  count: number;
  expandable?: boolean;
  isCollapsed?: boolean;
  isSelected: boolean;
  /// Collapsed over the lit space, so this row is the only sign of it.
  holdsSelection?: boolean;
  onToggleCollapse?: () => void;
  onOpenSettings: () => void;
  /// Its "+" opened the naming card, so the "+" stays up while it is open.
  isNamingInside: boolean;
  onCreateInside: () => void;
  onSelect: () => void;
  match?: RowMatch;
}> = ({
  path,
  count,
  expandable,
  isCollapsed,
  isSelected,
  holdsSelection,
  onToggleCollapse,
  onOpenSettings,
  isNamingInside,
  onCreateInside,
  onSelect,
  match,
}) => {
  const { space, ancestors } = path;
  const nameRef = useRef<HTMLSpanElement>(null);
  // The rail scrolls a deep tree sideways, so the lit name is brought into
  // view; while filtering, the match the keys are on.
  const inView = match ? match.isActive : isSelected;
  useEffect(() => {
    if (inView)
      nameRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [inView]);

  const fill = isSelected
    ? match?.isActive
      ? "bg-list-selected"
      : "bg-list-selected-light"
    : match?.isActive
      ? "bg-list-hover"
      : "bg-bg-subtle hover:bg-list-hover";
  const nameTone =
    isSelected || (match && !match.isContext)
      ? "text-fg-regular"
      : "text-fg-muted-regular group-hover:text-[var(--color-fg)]";

  return (
    <div
      data-space-id={space.space_id}
      onMouseEnter={match?.onHover}
      className={`group flex h-7 items-center gap-1.5 rounded-app transition-colors ${fill}`}
      style={{
        paddingLeft: `calc(var(--spacing-sm) + ${ancestors.length} * var(--spacing-md))`,
        transitionDuration: "var(--duration-micro)",
      }}
    >
      {expandable ? (
        <button
          aria-label={isCollapsed ? "Expand" : "Collapse"}
          aria-expanded={!isCollapsed}
          onClick={onToggleCollapse}
          className="flex h-3 w-3 shrink-0 items-center justify-center text-fg-muted opacity-65 hover:opacity-100"
        >
          <ChevronRightIcon
            sx={{
              fontSize: 12,
              transform: isCollapsed ? "none" : "rotate(90deg)",
              transition: TURN,
            }}
          />
        </button>
      ) : match?.openBelow ? (
        <span className="flex h-3 w-3 shrink-0 items-center justify-center text-fg-muted opacity-35">
          <ChevronRightIcon sx={{ fontSize: 12, transform: "rotate(90deg)" }} />
        </span>
      ) : (
        <span className="h-3 w-3 shrink-0" />
      )}
      <button
        onClick={onSelect}
        aria-current={isSelected ? "page" : undefined}
        className="flex h-full min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        <span data-naming-icon className="flex shrink-0">
          <SpaceDot
            color={spaceColor(space)}
            className={match?.isContext ? "opacity-55" : undefined}
          />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            ref={nameRef}
            data-naming-name
            className={`min-w-0 max-w-40 truncate ${nameTone}`}
          >
            {match
              ? highlightSegments(space.name, match.pattern).map((seg, i) =>
                  seg.hit ? (
                    <mark key={i} className="bg-transparent text-accent-medium">
                      {seg.text}
                    </mark>
                  ) : (
                    seg.text
                  ),
                )
              : space.name}
          </span>
          {space.sealed && (
            <Tooltip
              title={"Sealed space\nInvisible without a grant · nothing leaves"}
            >
              <LockOutlinedIcon
                sx={{ fontSize: 11 }}
                className="shrink-0 text-fg-muted"
              />
            </Tooltip>
          )}
          {holdsSelection && (
            <span
              role="img"
              aria-label="The selected space is inside"
              className="h-1 w-1 shrink-0 rounded-full bg-accent"
            />
          )}
        </span>
        {/* Only an org governor is served the count, so only they see this —
            an abandoned space has no manager left to show it to. */}
        {isAbandoned(space) && (
          <Tooltip title={"No manager\nOpen the space settings to assign one"}>
            <PersonOffOutlinedIcon
              sx={{ fontSize: 11 }}
              className="shrink-0 text-warning"
              aria-label="No manager"
            />
          </Tooltip>
        )}
      </button>
      {/* A deep tree scrolls the rail sideways; while in use, the controls
          come to its edge, over the name. */}
      <span
        className={`${isNamingInside ? "sticky" : "group-hover:sticky has-[:focus-visible]:sticky"} right-0 flex shrink-0 items-center gap-1 rounded-r-app bg-inherit pl-2 pr-1`}
        // Follows the row's fill as it animates; the global 0.2s colour
        // transition would trail it and show as a box behind the buttons.
        style={{ transition: "none" }}
      >
        <span
          className={`flex shrink-0 items-center gap-1 transition-opacity ${
            isNamingInside
              ? ""
              : "opacity-0 group-hover:opacity-100 has-[:focus-visible]:opacity-100"
          }`}
        >
          <TooltipButton
            title="New space"
            aria-label={`New space inside ${space.name}`}
            aria-haspopup="dialog"
            aria-expanded={isNamingInside}
            variant="contextual"
            className="rail-action"
            size="xs"
            onClick={onCreateInside}
          >
            <AddIcon fontSize="inherit" />
          </TooltipButton>
          <TooltipButton
            title="Space settings"
            aria-label={`Settings for ${space.name}`}
            variant="contextual"
            className="rail-action"
            size="xs"
            onClick={onOpenSettings}
          >
            <SettingsOutlinedIcon fontSize="inherit" />
          </TooltipButton>
        </span>
        {/* The buttons pad their icons by 3px; the count takes the same, so it
            sits as far from settings as settings does from +. */}
        {count > 0 && (
          <span
            className="shrink-0 pl-0.75 font-mono text-fg-muted opacity-70"
            style={{
              fontSize: "var(--font-size-x-small)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {count}
          </span>
        )}
      </span>
    </div>
  );
};
